/**
 * Build step 11 — the client deck (operator decision 19; prototype `VIEWS.deck`). Three endpoints: generate a deck
 * for a client and period, read it back, and get a short-lived link to it as PDF or PowerPoint.
 *
 * Who: whoever can open the client — managers (founder, ops AD) for every client, an ops member for the clients they
 * are the current SPOC of. Anyone else, and a member asking about someone else's client, gets 404 (the client's and
 * the deck's existence are not confirmed). Sales and marketing do not open client pages (the prototype hides "deck"
 * from the requester role), so they have no route here. The body is the same for every caller and carries no revenue.
 */
import { IdempotencyHeaders } from './common.js';
import { ClientDeck, ClientDeckParams, CreateDeckBody, DeckFileQuery } from './deck.js';
import { ClientIdParams } from './clients.js';
import { MediaUrlResponse } from './meetings.js';
import { API_BASE, defineRoute } from './route.js';
const CLIENT_READ = ['clients.read.all', 'clients.read.own'];
const SCOPE = 'Managers; or the client’s current SPOC. Others: 404.';
export const step11Routes = {
    'decks.create': defineRoute({
        id: 'decks.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/decks`,
        summary: 'Generate deck: a review deck for this client and period from tasks, requests and usage (never revenue)',
        description: 'Builds the slides now, in this request, and stores them as a snapshot (audited), so the preview and both downloads ' +
            'agree. 422 for a period that has not started. A period with no activity still makes a deck: its lists are empty ' +
            'and its numbers zero, never invented. Not while viewing as someone (403).',
        access: { auth: 'session', anyOf: CLIENT_READ, note: `${SCOPE} Not while viewing as someone (403). Audited.` },
        params: ClientIdParams,
        headers: IdempotencyHeaders,
        body: CreateDeckBody,
        success: {
            status: 201,
            description: 'The deck',
            schema: ClientDeck,
            headers: { Location: '/api/v1/client-decks/{id}' },
        },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'decks.get': defineRoute({
        id: 'decks.get',
        method: 'GET',
        path: `${API_BASE}/client-decks/{id}`,
        summary: 'One generated deck, as it was generated (the preview)',
        description: 'Visible to whoever can open its client now; after a SPOC change the old SPOC gets 404.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: SCOPE },
        params: ClientDeckParams,
        success: { status: 200, description: 'The deck', schema: ClientDeck },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'decks.file': defineRoute({
        id: 'decks.file',
        method: 'GET',
        path: `${API_BASE}/client-decks/{id}/file`,
        summary: 'Download PDF / Download PowerPoint: a short-lived link to the deck as a file',
        description: 'Rendered from the stored snapshot on the first request for each format, then kept, so a second download is the ' +
            'same file. 16:9 slides, the prototype’s layout, "Amplispot" in the corner. The link is same-origin ' +
            '(`/api/v1/files/{token}`, see `files.get`), works only for the signed-in person it was issued to and is sent as ' +
            'an attachment named "<client> — <Monthly|Quarterly> review <period>.pdf|.pptx". 503 when file storage is down.',
        access: { auth: 'session', anyOf: CLIENT_READ, note: SCOPE },
        params: ClientDeckParams,
        query: DeckFileQuery,
        success: { status: 200, description: 'Same-origin file link (expires in 10 minutes)', schema: MediaUrlResponse },
        errors: ['no_session', 'not_found', 'validation_failed', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.step11.js.map